import type { BooleanFlag, GeneralID, SceneType } from './baseTypes';
import type { CustomFeature } from './featureTypes';
import type { HSEdge, HSTitleSetting } from './hotspotTypes';
import type { LibMatrixCache } from './libTypes';

export type ST = STMap | STImage | STMatrix;

/** 沙盘类型 */
export enum SandTableType {
  image = 0,
  matrix = 1,
  map = 2,
}

/** 图片沙盘 */
export interface STImage {
  id: string;
  type: SandTableType.image;
  mediaId: number;
  mediaUrl: string;
  name: string;
  /** 沙盘图偏移角度, 默认:0 */
  angle?: number;
  /** 显示沙盘图指北针 - 0:不显示, 1:显示, 默认:0 */
  showCompass: BooleanFlag;
  /** 缩略图仅显示当前场景标记点 - 0:关闭, 1:开启, 默认:0 */
  showCurrentSceneOnly: BooleanFlag;
  /** 标记点 */
  markers: STImageMarker[];
  /** 分组高级设置 */
  groupSettings?: SandTableGroupSettings;
  /** 分组列表 */
  groupList?: SandTableImageGroupItem[];
  /** 默认分组 */
  groupDefaultId?: string;
}

/** 高清矩阵沙盘 */
export interface STMatrix {
  id: string;
  type: SandTableType.matrix;
  /** 矩阵素材 id */
  data: number;
  name: string;
  /** 沙盘图偏移角度, 默认:0 */
  angle?: number;
  /** 显示沙盘图指北针 - 0:不显示, 1:显示, 默认:0 */
  showCompass: BooleanFlag;
  /** 缩略图仅显示当前场景标记点 - 0:关闭, 1:开启, 默认:0 */
  showCurrentSceneOnly: BooleanFlag;
  /** 标记点 */
  markers: STImageMarker[];
  /** 分组高级设置 */
  groupSettings?: SandTableGroupSettings;
  /** 分组列表 */
  groupList?: SandTableImageGroupItem[];
  /** 默认分组 */
  groupDefaultId?: string;
}

export type STMatrixData = LibMatrixCache;

export enum STImageMarkerType {
  /** 场景标记 */
  scene = 0,
}

/** 图片沙盘标记点 */
export type STImageMarker = STImageMarkerScene;

/** 图片沙盘标记点 - 场景标记 */
export interface STImageMarkerScene {
  /** 标记类型, 目前只有场景标记, 存量数据为`0`; v5.8.10 */
  type?: STImageMarkerType;
  id: string;
  title: string;
  showTitle: BooleanFlag;
  titleSetting: HSTitleSetting;
  icon: {
    /** 图标类型: `0`圆点, `1`图标; 默认`0` */
    type: 0 | 1;
    /** 是否显示雷达光: `1`显示, `0`不显示; 默认`1` */
    showRadar: BooleanFlag;
    ath: number;
    atv: number;
    /** 宽度缩放 */
    scaleX: number;
    /** 高度缩放 */
    scaleY: number;
    /** 填充颜色 RGBA */
    fillColor: string;
    /** 边框颜色 '0,0,0,1' RGBA */
    borderColor: string;
    /** 填充颜色 RGBA */
    activeFillColor: string;
    /** 边框颜色 '0,0,0,1' RGBA */
    activeBorderColor: string;
    /** 自定义图标, v5.8.10 */
    mediaIcon?: {
      /** 图标类型: `0`系统图标, `1`素材库图标 */
      type: 0 | 1;
      /** 图标素材id */
      id: number;
      /** 图标素材URL */
      url: string;
      /** 图标锚点: 默认`bottom` */
      anchor: HSEdge;
    };
  };
  data: Partial<{
    /** 场景标记类型, 0: 场景切换, 默认: 0 */
    type: 0;
    sceneId: GeneralID;
    sceneType: SceneType;
  }>;
}

/** 地图沙盘数据 */
export interface STMap {
  id: string;
  type: SandTableType.map;
  name: string;
  /** 默认图层 - 0:普通, 1:卫星图, 默认:0 */
  layerType: 0 | 1;
  /** 高德地图样式 v5.2.7 */
  layerStyle?:
    | 'normal'
    | 'dark'
    | 'light'
    | 'whitesmoke'
    | 'fresh'
    | 'grey'
    | 'graffiti'
    | 'macaron'
    | 'blue'
    | 'darkblue'
    | 'wine';
  /** 缩略图仅显示当前场景标记点 - 0:关闭, 1:开启, 默认:0 */
  showCurrentSceneOnly: BooleanFlag;
  /** 地图初始显示模式 */
  initialView?: STMapInitialView;
  /** 标记点 */
  markers: STMapMarker[];
  /** 分组高级设置 */
  groupSettings?: SandTableGroupSettings;
  /** 分组列表 */
  groupList?: SandTableMapGroupItem[];
  /** 默认分组 */
  groupDefaultId?: string;
}

/** 地图初始显示模式 */
export type STMapInitialView =
  | {
      /** 0: 自动缩放显示全部标记点 */
      type: 0;
    }
  | {
      /** 1: 自定义地图显示区域 */
      type: 1;
      lng: number;
      lat: number;
      zoom: number;
    };

export enum STMapMarkerType {
  /** 场景标记 */
  scene = 0,
  /** 点标记 */
  point = 1,
  /** 文本标记 */
  text = 2,
  /** 贴图标记 */
  image = 3,
  /** 折线标记 */
  polyline = 4,
  /** 圆形标记 */
  circle = 5,
  /** 多边形标记 */
  polygon = 6,
  /** 脉冲标记 */
  pulse = 7,
}

/** 地图沙盘标记点 */
export type STMapMarker =
  | STMapMarkerScene
  | STMapMarkerFeature
  | STMapMarkerPulse;

/** 地图沙盘功能热点 */
export type STMapMarkerFeature =
  | STMapMarkerPoint
  | STMapMarkerText
  | STMapMarkerImage
  | STMapMarkerPolyline
  | STMapMarkerCircle
  | STMapMarkerPolygon;

/** 地图沙盘标记点 - 场景标记 */
export interface STMapMarkerScene {
  type?: STMapMarkerType.scene;
  id: string;
  title: string;
  showTitle: BooleanFlag;
  titleSetting: HSTitleSetting;
  icon: {
    /** 图标类型: `0`圆点, `1`图标; 默认`0` */
    type: 0 | 1;
    /** 是否显示雷达光: `1`显示, `0`不显示; 默认`1` */
    showRadar: BooleanFlag;
    lng: number;
    lat: number;
    /** 宽度缩放 */
    scaleX: number;
    /** 高度缩放 */
    scaleY: number;
    /** 填充颜色 RGBA */
    fillColor: string;
    /** 边框颜色 '0,0,0,1' RGBA */
    borderColor: string;
    /** 填充颜色 RGBA */
    activeFillColor: string;
    /** 边框颜色 '0,0,0,1' RGBA */
    activeBorderColor: string;
    /** 自定义图标, v5.8.10 */
    mediaIcon?: {
      /** 图标类型: `0`系统图标, `1`素材库图标 */
      type: 0 | 1;
      /** 图标素材id */
      id: number;
      /** 图标素材URL */
      url: string;
      /** 图标锚点: 默认`bottom-center` */
      anchor:
        | 'top-left'
        | 'top-center'
        | 'top-right'
        | 'middle-left'
        | 'center'
        | 'middle-right'
        | 'bottom-left'
        | 'bottom-center'
        | 'bottom-right';
    };
  };
  data: Partial<{
    /** 场景标记类型, 0: 场景切换, 默认: 0 */
    type: 0;
    sceneId: GeneralID;
    sceneType: SceneType;
  }>;
}

/** 沙盘场景标记功能`data` */
export type STSceneMarkerData = Partial<{
  /** 场景标记类型, 0: 场景切换, 默认: 0 */
  type: 0;
  sceneId: GeneralID;
  sceneType: SceneType;
}>;

/** 地图沙盘标记点 - 点标记 */
export interface STMapMarkerPoint {
  type: STMapMarkerType.point;
  id: string;
  title: string;
  showTitle: BooleanFlag;
  titleSetting: HSTitleSetting;
  icon: STMapMarkerScene['icon'];
  data: CustomFeature;
}

/** 地图沙盘标记点 - 文本标记 */
export interface STMapMarkerText {
  type: STMapMarkerType.text;
  id: string;
  title: string;
  titleSetting: HSTitleSetting;
  icon: {
    lng: number;
    lat: number;
    zoom: number;
    /** 是否跟随画面同步缩放; 默认`0` */
    stick: BooleanFlag;
  };
  data: CustomFeature;
}

/** 地图沙盘标记点 - 贴图标记 */
export interface STMapMarkerImage {
  type: STMapMarkerType.image;
  id: string;
  title: string;
  icon: {
    lng: number;
    lat: number;
    zoom: number;
    /** 宽度缩放, 0.1 - 2, 默认`1` */
    scaleX: number;
    /** 高度缩放, 0.1 - 2, 默认`1` */
    scaleY: number;
    /** 贴图 */
    image: {
      /** 图片类型: `0`系统图片, `1`素材库图片 */
      type: 0 | 1;
      /** 图片素材id */
      id: number;
      /** 图片素材URL */
      url: string;
    };
  };
  data: CustomFeature;
}

/** 地图沙盘标记点 - 折线标记 */
export interface STMapMarkerPolyline {
  type: STMapMarkerType.polyline;
  id: string;
  title: string;
  icon: {
    /** 折线路径 */
    path: Array<[number, number]>;
    /** 线条样式 */
    strokeStyle: {
      /** 线条类型: 实线`solid`, 虚线`dashed`; 默认`solid` */
      type: 'solid' | 'dashed';
      /** 线条颜色, 默认`#286EFA` */
      color: string;
      /** 线条宽度, 1-20, 默认`2` */
      width: number;
      /** 线条透明度 默认`1` */
      opacity: number;
    };
    /** 边框样式 */
    borderStyle: {
      /** 边框颜色, 默认`#1A1A1A` */
      color: string;
      /** 边框宽度, 0-10, 默认`0` */
      width: number;
    };
    /** 动效 */
    animation: {
      /** 动效类型, `0`无动效, `1`轨迹运动; 默认`0` */
      type: 0 | 1;
      /** 轨迹运动图标地址 */
      markerIcon: string;
      markerWidth: number;
      markerHeight: number;
      /** 图标缩放, 0.1 - 2, 默认`0.5` */
      markerScale?: number;
    };
  };
  data: CustomFeature;
}

/** 地图沙盘标记点 - 脉冲线标记 */
export interface STMapMarkerPulse {
  type: STMapMarkerType.pulse;
  id: string;
  title: string;
  icon: {
    /** 折线路径 */
    path: Array<[number, number]>;
    /** 脉冲样式 */
    pulseStyle: {
      /** 头部颜色, 默认`#286EFA` */
      headColor: string;
      /** 头部透明度, 默认`1` */
      headOpacity: number;
      /** 尾部颜色, 默认`#D6E4EF` */
      tailColor: string;
      /** 尾部透明度, 默认`1` */
      tailOpacity: number;
      /** 线条宽度, 1-20, 默认`2` */
      width: number;
    };
    /** 动效 */
    animation: STMapMarkerPolyline['icon']['animation'];
  };
}

/** 地图沙盘标记点 - 圆形标记 */
export interface STMapMarkerCircle {
  type: STMapMarkerType.circle;
  id: string;
  title: string;
  icon: {
    lng: number;
    lat: number;
    /** 半径, 0-50000 */
    radius: number;
    /** 是否闪烁, 默认`0` */
    blink: BooleanFlag;
    /** 填充样式 */
    fillStyle: {
      /** 填充颜色, 默认`#286EFA` */
      color: string;
      /** 填充透明度, 默认`0.2` */
      opacity: number;
    };
    /** 边框样式 */
    borderStyle: {
      /** 边框颜色, 默认`#286EFA` */
      color: string;
      /** 边框透明度, 默认`1` */
      opacity: number;
      /** 边框宽度, 1-20, 默认`2` */
      width: number;
    };
    /** 填充样式 - 鼠标移入状态 */
    fillStyleActive: {
      /** 填充颜色, 默认`#286EFA` */
      color: string;
      /** 填充透明度, 默认`0.5` */
      opacity: number;
    };
    /** 边框样式 - 鼠标移入状态 */
    borderStyleActive: {
      /** 边框颜色, 默认`#286EFA` */
      color: string;
      /** 边框透明度, 默认`1` */
      opacity: number;
      /** 边框宽度, 1-20, 默认`2` */
      width: number;
    };
  };
  data: CustomFeature;
}

/** 地图沙盘标记点 - 多边形标记 */
export interface STMapMarkerPolygon {
  type: STMapMarkerType.polygon;
  id: string;
  title: string;
  icon: {
    /** 节点坐标 */
    path: Array<[number, number]>;
    /** 是否闪烁, 默认`0` */
    blink: BooleanFlag;
    /** 填充样式 */
    fillStyle: STMapMarkerCircle['icon']['fillStyle'];
    /** 边框样式 */
    borderStyle: STMapMarkerCircle['icon']['borderStyle'];
    /** 填充样式 - 鼠标移入状态 */
    fillStyleActive: STMapMarkerCircle['icon']['fillStyleActive'];
    /** 边框样式 - 鼠标移入状态 */
    borderStyleActive: STMapMarkerCircle['icon']['borderStyleActive'];
    /** 动效 */
    animation: STMapMarkerPolyline['icon']['animation'];
  };
  data: CustomFeature;
}

// 分组相关
export type SandTableGroupSettings = Partial<{
  /** 分组类型: `0`纯文字分组, `1`文字图标分组, 默认`0` */
  type: 0 | 1;
  /** 背景颜色, 默认"219,219,219,0.5" */
  bgColor: string;
  /** 字体颜色, 默认"26,26,26,1" */
  fontColor: string;
  /** 选中字体颜色, 默认"250,100,0,1" */
  activeFontColor: string;
}>;

export interface SandTableMapGroupItem {
  id: string;
  name: string;
  /** 视角设置 */
  view: {
    /** 自定义分组视角开关: 0 | 1，默认`0` */
    type: BooleanFlag;
    lng?: number;
    lat?: number;
    zoom?: number;
    /** 分组视角缩放开关: 0 | 1, 默认`0` */
    changeZoom?: BooleanFlag;
  };
  /** 分组标记点 */
  markers: {
    /** 分组热点类型: `0`手动选择，`1`包含全部, 默认`0` */
    type: 0 | 1;
    /** 标记的id列表 */
    list: string[];
  };
  /** 分组图标 */
  icon: {
    /** 图标类型: `1`素材库图标, `2`系统SVG图标 */
    type: 1 | 2;
    /** 图标素材id */
    id: number;
    /** 图标素材url */
    url: string;
  };
}

export interface SandTableImageGroupItem {
  id: string;
  name: string;
  /** 视角设置 */
  view: {
    /** 自定义分组视角开关: 0 | 1，默认`0` */
    type: BooleanFlag;
    hlookat?: number;
    vlookat?: number;
    fov?: number;
    /** 分组视角缩放开关: 0 | 1, 默认`0` */
    changeFov?: BooleanFlag;
  };
  /** 分组标记点 */
  markers: {
    /** 分组热点类型: `0`手动选择，`1`包含全部, 默认`0` */
    type: 0 | 1;
    /** 标记的id列表 */
    list: string[];
  };
  /** 分组图标 */
  icon: {
    /** 图标类型: `1`素材库图标, `2`系统SVG图标 */
    type: 1 | 2;
    /** 图标素材id */
    id: number;
    /** 图标素材url */
    url: string;
  };
}
